import { RootState } from "../../../redux-store";
import { isNotNull, isNotNullOrUndefined } from "dora-shared";
import { RouteCargoStopNew } from "./types";
import { selectMe } from "../../auth/selectors";
import { createSelector } from "@reduxjs/toolkit";
import { selectClient } from "../clients/selectors";
import { DateTime } from "luxon";
import { ExpandedRow } from "./index";
import { reportWarningToBackend } from "../../api";

const selectRouteWithStops = (state: RootState) => state.data.routeWithStops;

export const selectIsRowExpanded = (identifier: ExpandedRow) =>
  createSelector(
    selectRouteWithStops,
    (data) =>
      data.expandedRow?.id === identifier.id &&
      data.expandedRow.type === identifier.type
  );

export const selectClientFromRoutesResponse = (clientId: string | null) =>
  createSelector(selectRouteWithStops, (state) => {
    if (!clientId || !state.data) {
      return null;
    }
    return state.data.clients.find((c) => c.id === clientId);
  });

export const selectInitialLoaded = createSelector(
  selectRouteWithStops,
  (routeWithStops) => routeWithStops.initialLoaded
);

export const selectRouteWithStopsData = createSelector(
  selectRouteWithStops,
  (routeWithStops) => {
    return routeWithStops.data;
  }
);

export const selectStopsAndWaypointsNumberNew = createSelector(
  selectRouteWithStopsData,
  (routeData) => routeData?.routeStops.length || 0
);

export const selectCargoTimeTracking = (cargoId: string) =>
  createSelector(selectCargo(cargoId), (cargo) => {
    if (!cargo) {
      return null;
    }
    return cargo.timeTracking;
  });

export const selectShowCompletedDispatcherStops = (state: RootState) =>
  state.data.routeWithStops.showCompletedDispatcherCreatedStops;

export const showHideCompletedStopsButtonNew = createSelector(
  selectRouteWithStopsData,
  (routeData) => {
    if (!routeData) {
      return false;
    }
    return routeData.routeStops.some((rs) => {
      if (
        rs.type === "CARGO_STOP" &&
        (rs.stop.completedAt || rs.stop.departedAt)
      ) {
        const cargoForStop = routeData.cargos.find((c) =>
          c.stops.includes(rs.stop.id)
        )!;
        return cargoForStop.createdBy === "DISPATCHER";
      }
      // if (rs.type === "WAYPOINT") {
      //   return true;
      // }
      return false;
    });
  }
);

export const selectStopsCreatedByDispatcher = createSelector(
  selectRouteWithStopsData,
  (routeData) => {
    if (!routeData) {
      return null;
    }

    const cargosCreatedByDispatcher = routeData.cargos.filter(
      (c) => c.createdBy === "DISPATCHER"
    );

    const cargoStopsCreatedByDispatcher = cargosCreatedByDispatcher.flatMap(
      (c) => c.stops
    );

    const routeStopsInOrderCreatedByDispatcher = routeData.routeStops.filter(
      (rs) =>
        rs.type === "WAYPOINT" ||
        cargoStopsCreatedByDispatcher.find((s) => s === rs.stop.id)
    );

    return routeStopsInOrderCreatedByDispatcher
      .map((rs) => {
        if (rs.type === "WAYPOINT") {
          return rs;
        } else {
          const routeStop = routeData.routeStops.find(
            (rss) => rss.type === "CARGO_STOP" && rss.stop.id === rs.stop.id
          ) as RouteCargoStopNew;
          if (!routeStop) {
            return null;
          }
          return {
            type: "CARGO_STOP" as const,
            stop: routeStop.stop,
          };
        }
      })
      .filter(isNotNullOrUndefined);
  }
);

export const selectCompletedStopsCreatedByDispatcher = createSelector(
  selectStopsCreatedByDispatcher,
  selectRouteWithStopsData,
  (stops, routeWithStops) => {
    if (!stops) {
      return null;
    }
    const result = [];
    for (const s of stops) {
      if (s.type === "WAYPOINT") {
        result.push(s);
      }
      if (s.type === "CARGO_STOP") {
        const driverPressedDepartedCompleted =
          s.stop.completedAt || s.stop.departedAt;
        const cargo = routeWithStops!.cargos.find((c) =>
          c.stops.includes(s.stop.id)
        )!;
        if (driverPressedDepartedCompleted) {
          if (!cargo.timeTracking) {
            result.push(s);
            continue;
          }

          const cargoStops = cargo.stops
            .map(
              (cs) =>
                routeWithStops!.routeStops.find(
                  (rs) => rs.type === "CARGO_STOP" && rs.stop.id === cs
                ) as RouteCargoStopNew
            )
            .filter(Boolean);
          let lastStopCompletedOrDeparted = null;
          const allStopsCompletedOrDeparted = cargoStops.every(
            (cs) => cs.stop.completedAt || cs.stop.departedAt
          );
          if (allStopsCompletedOrDeparted) {
            lastStopCompletedOrDeparted = cargoStops.reduce((acc: any, cs) => {
              const stopCompletedOrDepartedAt =
                cs.stop.completedAt || cs.stop.departedAt;
              if (!acc) {
                return cs;
              }
              const accCompletedOrDepartedAt =
                acc.stop.completedAt || acc.stop.departedAt;
              if (
                new Date(accCompletedOrDepartedAt!) >
                new Date(stopCompletedOrDepartedAt!)
              ) {
                return acc;
              }
              return cs;
            }, null);

            if (
              s.stop.id !== lastStopCompletedOrDeparted.stop.id ||
              cargo.timeTracking.ended
            ) {
              result.push(s);
            }
          } else {
            result.push(s);
          }
        }
      }
    }
    let actualEndPos = result.length;
    for (let i = result.length - 1; i >= 0; i--) {
      const s = result[i];
      if (s.type === "CARGO_STOP") {
        break;
      }
      actualEndPos--;
    }
    return result.slice(0, Math.max(actualEndPos, 0));
  }
);

export const selectCompletedStopsCreatedByDispatcherFilteredByShowHideButton =
  createSelector(
    selectCompletedStopsCreatedByDispatcher,
    selectShowCompletedDispatcherStops,
    (completedStops, showCompleted) => {
      if (!completedStops) {
        return null;
      }
      return showCompleted ? completedStops : [];
    }
  );

export const selectUpcomingStopsCreatedByDispatcher = createSelector(
  selectStopsCreatedByDispatcher,
  selectCompletedStopsCreatedByDispatcher,
  (allStops, completedStops) => {
    if (!allStops || !completedStops) {
      return null;
    }
    return allStops.filter((s) => !completedStops.includes(s));
  }
);

export const selectDriverCreatedCargos = createSelector(
  selectRouteWithStopsData,
  (routeData) => {
    if (!routeData) {
      return null;
    }

    const cargosCreatedByDrivers = routeData.cargos.filter(
      (c) => c.createdBy === "DRIVER"
    );
    return cargosCreatedByDrivers
      .sort(
        (a, b) =>
          DateTime.fromISO(a.createdAt).toMillis() -
          DateTime.fromISO(b.createdAt).toMillis()
      )
      .map((c) => ({
        ...c,
        stops: c.stops
          .map((s) => {
            const routeStop = routeData.routeStops.find(
              (rs) => rs.type === "CARGO_STOP" && rs.stop.id === s
            ) as RouteCargoStopNew;
            if (!routeStop) {
              reportWarningToBackend({
                err: `No route stop with id ${s} found`,
                routeData,
              });
              return null;
            }
            return routeStop.stop;
          })
          .filter(isNotNull),
      }));
  }
);

const selectClientForStopNew = (stopId: string) => (state: RootState) => {
  const cargo = state.data.routeWithStops.data?.cargos.find((c) =>
    c.stops.some((s) => s === stopId)
  )!;
  if (!cargo.clientId) {
    return null;
  }

  return selectClient(cargo.clientId)(state);
};

export const selectCanMoveCargoToUndispatched = (cargoId: string) =>
  createSelector(selectCargo(cargoId), selectMe, (cargo, me) => {
    if (!cargo || !me?.canAddUndispatchedCargos) {
      return false;
    }

    return cargo.driverCanMoveToUndispatched;
  });

export const selectImagesAddedOnCargo = (cargoId: string) =>
  createSelector(selectRouteWithStopsData, (routeData) => {
    if (!routeData) {
      return false;
    }
    const cargo = routeData.cargos.find((c) => c.id === cargoId);
    if (!cargo) {
      return false;
    }

    const cargoStops = cargo.stops
      .map(
        (cs) =>
          routeData.routeStops.find(
            (rs) => rs.type === "CARGO_STOP" && rs.stop.id === cs
          ) as RouteCargoStopNew
      )
      .filter(isNotNullOrUndefined);
    return cargoStops.some((cs) => cs.stop.attachments.length > 0);
  });

export const selectShouldShowArrivalDepartureButtonsNew =
  (stopId: string) => (state: RootState) => {
    const enabledForOrg =
      !!selectMe(state)?.arrivedDepartedButtonsDefaultEnabledForOrg;
    if (!enabledForOrg) {
      return false;
    }

    const client = selectClientForStopNew(stopId)(state);
    if (!client) {
      return true;
    }
    if (client?.showArrivalDepartureToDriver === null) {
      // either not set on customer OR no customer for cargo
      return true;
    }
    return client?.showArrivalDepartureToDriver;
  };

export const selectShouldShowCubicMetersButtonNew =
  (stopId: string) => (state: RootState) => {
    const enabledForOrg =
      !!selectMe(state)?.cubicMetersButtonDefaultEnabledForOrg;
    if (!enabledForOrg) {
      return false;
    }

    const client = selectClientForStopNew(stopId)(state);
    if (client === null || client?.showAddCubicMetersToDriver === null) {
      // either not set on customer OR no customer for cargo
      return true;
    }
    return client?.showAddCubicMetersToDriver;
  };

export const selectCargo = (cargoId: string) =>
  createSelector(selectRouteWithStopsData, (routeData) => {
    if (!routeData) {
      return null;
    }

    return routeData.cargos.find((c) => c.id === cargoId);
  });

const selectCargoForStop = (stopId: string) =>
  createSelector(selectRouteWithStopsData, (routeData) => {
    if (!routeData) {
      return null;
    }
    return routeData.cargos.find((c) => c.stops.includes(stopId));
  });

export const selectStopsActedOnFromSameCargo = (cargoId: string) =>
  createSelector(selectRouteWithStopsData, (routeData) => {
    if (!routeData) {
      return [];
    }
    const cargo = routeData.cargos.find((c) => c.id === cargoId)!;
    const cargoStops = cargo.stops.map(
      (cargoStopId) =>
        routeData.routeStops.find(
          (rs) => rs.type === "CARGO_STOP" && rs.stop.id === cargoStopId
        ) as RouteCargoStopNew
    );

    const cargoStopsActedOn = cargoStops
      .map((rs) => {
        if (!rs) {
          // normally should not get this, check the whole commit to see what is happening
          return null;
        }
        const stop = rs.stop;
        const actedOnDates = [
          stop.arrivedAt,
          stop.departedAt,
          stop.completedAt,
        ].filter(Boolean);
        if (!actedOnDates.length) {
          return null;
        }
        const date = DateTime.min(
          ...actedOnDates.map((d) => DateTime.fromISO(d!))
        );
        return {
          date,
          stop,
        };
      })
      .filter(Boolean);

    return cargoStopsActedOn.sort(
      (a, b) => (a?.date?.toMillis() || 0) - (b?.date?.toMillis() || 0)
    );
  });

export const selectUploadedDocumentsForCargo = (cargoId: string) =>
  createSelector(
    selectRouteWithStopsData,
    (data) =>
      data?.routeStops
        .filter((rs) => rs.type === "CARGO_STOP")
        .filter((rs) => rs.stop.cargoId === cargoId)
        .flatMap((stop) => stop.stop.attachments) || []
  );


export const selectErrorReason = createSelector(
  selectRouteWithStops,
  (x) => x.errorReason
);

const selectRouteStop = (stopId: string) =>
  createSelector(selectRouteWithStopsData, (data) => {
    if (!data) {
      return null;
    }
    return data.routeStops.find(
      (s) => s.type === "CARGO_STOP" && s.stop.id === stopId
    ) as RouteCargoStopNew;
  });

export const selectDriverArrivedAtStopFromActivities = (stopId: string) =>
  createSelector(selectRouteStop(stopId), (routeStop) => {
    if (!routeStop) {
      return null;
    }
    if (!routeStop.stop.arrivedAt) {
      return null;
    }
    return DateTime.fromISO(routeStop.stop.arrivedAt);
  });

export const selectDriverDepartedAtStopFromActivities = (stopId: string) =>
  createSelector(selectRouteStop(stopId), (routeStop) => {
    if (!routeStop) {
      return null;
    }
    if (!routeStop.stop.departedAt) {
      return null;
    }
    return DateTime.fromISO(routeStop.stop.departedAt);
  });

export const selectStopCommentsFromActivities = (stopId: string) =>
  createSelector(selectRouteStop(stopId), (routeStop) => {
    if (!routeStop) {
      return [];
    }
    return routeStop.stop.comments;
  });

export const selectStopVolumesFromActivities = (stopId: string) =>
  createSelector(selectRouteStop(stopId), (routeStop) => {
    if (!routeStop) {
      return [];
    }
    return routeStop.stop.volumes;
  });

export const selectStopWeightsFromActivities = (stopId: string) =>
  createSelector(selectRouteStop(stopId), (routeStop) => {
    if (!routeStop) {
      return [];
    }
    return routeStop.stop.weights;
  });

export const selectStopInvoiceRefsFromActivities = (stopId: string) =>
  createSelector(selectRouteStop(stopId), (routeStop) => {
    if (!routeStop) {
      return [];
    }
    return routeStop.stop.recordedRefs;
  });

export const selectRelatedStopsFromCargo =
  (stopId: string, cargoId: string) => (state: RootState) => {
    const routeData = selectRouteWithStopsData(state);
    if (!routeData) {
      return { pickups: [], dropoffs: [] };
    }

    const cargo = routeData.cargos.find((c) => c.id === cargoId);
    if (!cargo) {
      return { pickups: [], dropoffs: [] };
    }

    const stops = cargo.stops
      .filter((s) => s !== stopId)
      .map((stopIdInCargo) =>
        routeData.routeStops.find(
          (rs) => rs.type === "CARGO_STOP" && rs.stop.id === stopIdInCargo
        )
      )
      .filter(isNotNullOrUndefined)
      .map((rs) => (rs as RouteCargoStopNew).stop);

    return {
      pickups: stops.filter((s) => s.type === "PICKUP"),
      dropoffs: stops.filter((s) => s.type === "DROPOFF"),
    };
  };

// Total pickup/dropoff counts for a cargo, regardless of which stop (if any)
// is currently being viewed - used to tell whether adding one more dropoff
// would land the cargo on >1 pickups and >1 dropoffs at once (many-to-many),
// which the server rejects unless the "pickups-dropoffs-many-to-many"
// feature is enabled.
export const selectCargoStopCounts = (cargoId: string) =>
  createSelector(selectRouteWithStopsData, (data) => {
    const cargoStops = (data?.routeStops ?? []).filter(
      (rs): rs is RouteCargoStopNew =>
        rs.type === "CARGO_STOP" && rs.stop.cargoId === cargoId
    );
    return {
      pickups: cargoStops.filter((rs) => rs.stop.type === "PICKUP").length,
      dropoffs: cargoStops.filter((rs) => rs.stop.type === "DROPOFF").length,
    };
  });

export const selectStopCompletedDate = (stopId: string) =>
  createSelector(selectRouteStop(stopId), (routeStop) => {
    if (!routeStop) {
      return null;
    }
    if (!routeStop.stop.completedAt) {
      return null;
    }
    return DateTime.fromISO(routeStop.stop.completedAt);
  });

// A cargo can have several pickups (e.g. consolidating multiple pickups into
// one dropoff). A new dropoff must not land before the *last* of them - this
// picks the latest pickup by date/time, not just the first one found, so that
// date/time validation (e.g. in AddDropoffDialog) mirrors the server's
// `ERR_PICKUP_AFTER_DROPOFF` rule instead of comparing against an arbitrary
// pickup.
export const selectCargoPickup = (cargoId: string) =>
  createSelector(selectRouteWithStopsData, (data) => {
    const pickupStops = data!.routeStops.filter(
      (rs): rs is RouteCargoStopNew =>
        rs.type === "CARGO_STOP" &&
        rs.stop.cargoId === cargoId &&
        rs.stop.type === "PICKUP"
    );
    return pickupStops.reduce((latest, s) => {
      if (!latest) {
        return s.stop;
      }
      if (s.stop.date !== latest.date) {
        return s.stop.date > latest.date ? s.stop : latest;
      }
      return (s.stop.time ?? "") > (latest.time ?? "") ? s.stop : latest;
    }, pickupStops[0]?.stop);
  });

export const selectDriverCanDeleteCargo = (cargoId: string) =>
  createSelector(selectMe, selectCargo(cargoId), (driver, cargo) => {
    if (!driver?.canDeleteOwnCargoStops || !cargo) {
      return false;
    }
    return cargo.requestingDriverCanDeleteCargo;
  });

export const selectDriverDeleteStopIsDisabled = (stopId: string) =>
  createSelector(selectCargoForStop(stopId), (cargo) => {
    if (!cargo || !cargo.requestingDriverCanDeleteCargo) {
      return true;
    }
    return cargo.stops.length > 1 && cargo.stops[0] === stopId;
  });

export const selectReceivedDataInvalid = createSelector(
  selectRouteWithStops,
  (data) => !data.receivedDataValid
);
